import React, { FC, useContext, useEffect, useState } from "react";
import { DeviceModelType, DeviceSummaryType, DeviceType, PaginationContainer } from "../../../utils/types/types";
import { Button, Divider, Form, Image, Input, List, message, Select, Steps, Tag } from "antd";
import { createDevice, getDeviceSummary, getModels } from "../../../services/services";
import { NoParamVoidType, ResponseType } from "../../../utils/types/uiTypes";
import { ModelCard } from "../../models/ModelCard";
import { PRIMARY_BUTTON_STYLE } from "../../../utils/general/styles";
import { find, groupBy, path, propEq, values } from "ramda";
import { ApiOutlined, ContainerOutlined, PrinterOutlined } from "@ant-design/icons";
import { formItemRequiredRule } from "../../../utils/forms";
import { DeviceConnections } from "../details/DeviceConnections";
import { useHistory } from "react-router";
import { SearchBar } from "../../general/SearchBar";
import { AuthenticationContext } from "../../../context/AuthenticationContext";
import { PageLayout } from "../../layout/PageLayout";
import Title from "antd/lib/typography/Title";
import { useLocation } from "react-router-dom";
import { useForm } from "antd/es/form/Form";
import { Spinner } from "../../general/Spinner";
import moment from "moment";
import { getConnectionStatus, STATUS_STYLE } from "../../../utils/connection";
import { DeviceStatusType } from "../../../utils/types/enums";

const { Step } = Steps;

const getStepStatus = (currentIndex: number, stepIndex: number) => {
  if (stepIndex === currentIndex) {
    return "process";
  } else if (stepIndex < currentIndex) {
    return "finish";
  } else {
    return "wait";
  }
};

const groupByManufacturer = groupBy<DeviceModelType>((model) => model?.manufacturer.id);

export const AddDevice: FC = function () {
  const { authentication } = useContext(AuthenticationContext);
  const history = useHistory();
  const { search } = useLocation();
  const [form] = useForm();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState<LoadingType | null>("INITIAL_DATA");
  const urlSearchParams = new URLSearchParams(search);
  const [deviceSummary, setDeviceSummary] = useState<DeviceSummaryType | null>(null);
  const [selectedModel, setSelectedModel] = useState<DeviceModelType | null>(null);
  const [models, setModels] = useState<Record<string, DeviceModelType[]> | null>(null);

  const fetchModels: NoParamVoidType = () => {
    getModels({})
      .then(({ data }: ResponseType<PaginationContainer<DeviceModelType>>) => {
        setModels(groupByManufacturer(data.results));
      })
      .catch((error: any) => {
        console.error(error);
      })
      .then(() => {
        setLoading(null);
      });
  };

  const getSummary: getSummaryType = (id) => {
    getDeviceSummary({
      segments: {
        id,
      },
      params: {
        from: moment(Date.now()).subtract(1, "h").valueOf(),
        to: moment(Date.now()).valueOf(),
      },
    })
      .then(({ data }: ResponseType<DeviceSummaryType>) => {
        const { device, jobs = [], widgets = [], messages = [], monitors = [] } = data;
        setDeviceSummary({ device, jobs, widgets, messages, monitors });
      })
      .catch((error: any) => {
        console.error(error);
      })
      .then(() => {
        setLoading(null);
        setStep(2);
      });
  };

  const addDevice = (data: any) => {
    createDevice({
      body: JSON.stringify(data),
    })
      .then(({ data }: ResponseType<DeviceType>) => {
        getSummary(data.id);
        message.success("Device has been added successfully!");
      })
      .catch(() => {
        setLoading(null);
        message.error("Unable to add the device. Please try again later!");
      });
  };

  useEffect(() => {
    fetchModels();
  }, []);

  useEffect(() => {
    form.setFields([
      { name: "name", value: `${selectedModel?.manufacturer.name} ${selectedModel?.name}` },
      { name: "tenantId", value: authentication ? path(["tenants", 0, "id"], authentication) : "" },
    ]);
  }, [selectedModel]);

  return (
    <PageLayout
      loading={loading === "INITIAL_DATA"}
      loadingText={"Loading Devices ..."}
      header={
        <div className={"bg-white z-[800] w-full"}>
          <div className={"mx-auto border-b shadow-bottom"}>
            <div className={`2xl:max-w-screen-2xl mx-auto bg-white flex justify-between items-center px-6 h-16`}>
              <div>
                <Title level={5} className={"text-dark-700 mb-0"}>
                  Add Device
                </Title>
              </div>
              <div className={"max-w-2xl flex-grow"}>
                <Steps size={"small"}>
                  <Step stepIndex={0} status={getStepStatus(step, 0)} title="Model" icon={<PrinterOutlined />} />
                  <Step stepIndex={1} status={getStepStatus(step, 1)} title="Device Details" icon={<ContainerOutlined />} />
                  <Step stepIndex={2} status={getStepStatus(step, 2)} title="Integrations" icon={<ApiOutlined />} />
                </Steps>
              </div>
              <div>
                <SearchBar
                  defaultValue={new URLSearchParams(location.search).get("query")?.toString()}
                  onSearch={(query) => {
                    urlSearchParams.set("query", query);
                    history.push({ pathname: location.pathname, search: urlSearchParams.toString() });
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      }
      footer={
        <div className={"bg-white z-[800] w-full"}>
          <div className={"2xl:max-w-screen-2xl mx-auto flex flex-row h-16 gap-2 justify-end items-center"}>
            <Button
              className={"w-28"}
              hidden={step === 0 || step === 2}
              disabled={step === 0}
              type={"default"}
              onClick={() => {
                setStep(0);
              }}
            >
              Previous
            </Button>
            <Button
              className={"w-28"}
              type={"default"}
              disabled={selectedModel === null || step === 1}
              hidden={step === 2}
              onClick={() => {
                setStep(1);
              }}
            >
              Next
            </Button>
            {deviceSummary && step === 2 && (
              <>
                <Button
                  className={`w-28 ${PRIMARY_BUTTON_STYLE}`}
                  type={"default"}
                  onClick={() => {
                    history.push(`/devices/${deviceSummary.device.id}/overview`);
                  }}
                >
                  Finish
                </Button>
              </>
            )}
          </div>
        </div>
      }
    >
      <div className={"relative h-full w-full"}>
        {step == 0 && (
          <div className={"h-full flex-grow overflow-y-auto hide-scrollbar pb-14"}>
            {models &&
              values(models)
                .filter((o) => o.length > 0)
                .map((v, i) => {
                  const filteredModels = v.filter((o) =>
                      JSON.stringify(o)
                          .toLowerCase()
                          .includes(urlSearchParams.get("query")?.toLowerCase() ?? "")
                  )
                  return (
                    <div key={i} className={`${filteredModels.length > 0 ? '' : 'hidden'}`}>
                      <div className={"font-semibold"}>{v[0].manufacturer?.name} </div>
                      <Divider className={"mt-2 mb-4"} type={"horizontal"} />
                      <List<DeviceModelType>
                        grid={{
                          gutter: 16,
                          xs: 2,
                          sm: 3,
                          md: 4,
                          lg: 6,
                          xl: 8,
                          xxl: 10,
                        }}
                        className={'p-2'}
                        dataSource={filteredModels}
                        renderItem={({ id, name, imageUrl }) => (
                          <List.Item
                            itemID={id}
                            onClick={() => {
                              const model = find<DeviceModelType>(propEq("id", id))(filteredModels);
                              model && setSelectedModel(model);
                            }}
                          >
                            <ModelCard selected={selectedModel?.id === id} image={imageUrl} name={name} />
                          </List.Item>
                        )}
                      />
                    </div>
                  );
                })}
          </div>
        )}
        {step === 1 && (
          <div className={"relative bg-white border shadow-md m-10 p-20 grid grid-cols-2 gap-4 max-w-screen-lg mx-auto items-center"}>
            <Spinner loading={loading === "ADD_DEVICE"} loadingText={"Adding Device ..."} />
            <div className={"h-full flex flex-col items-center gap-4"}>
              <Image
                preview={false}
                height={300}
                src={selectedModel?.imageUrl ?? "error"}
                fallback="data:image/png;base64,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"
              />
              <div className={"flex flex-col items-start justify-center gap-4 mr-4"}>
                <div className={"w-full text-base font-bold"}>
                  <span className={"text-primary-600 group"}>{selectedModel?.name}</span>
                </div>
                <div className={"flex flex-col gap-1"}>
                  <div className={"truncate overflow-ellipsis"}>
                    <div className={"inline-block w-44 pr-1 text-xs"}>Model ID</div>
                    <span className={"w-full truncate overflow-ellipsis font-light"}>
                      <Tag className={"text-xs"}>{selectedModel?.id}</Tag>
                    </span>
                  </div>
                  <div className={"truncate overflow-ellipsis"}>
                    <div className={"inline-block w-44 pr-1 text-xs"}>Type</div>
                    <span className={"w-full truncate overflow-ellipsis font-light text-xs"}>{selectedModel?.type}</span>
                  </div>
                  <div className={"truncate overflow-ellipsis"}>
                    <div className={"inline-block w-44 pr-1 text-xs"}>Manufacturer</div>
                    <span className={"w-full truncate overflow-ellipsis font-light text-xs"}>{selectedModel?.manufacturer?.name}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className={"flex flex-col h-full w-full py-12"}>
              <Form
                className={"w-full"}
                onFinish={(values) => {
                  addDevice({ ...values, model: selectedModel });
                }}
                form={form}
                layout={"vertical"}
                requiredMark={false}
              >
                <Form.Item className={"w-full"} name={"name"} label={<span className={"text-xs text-gray-400"}>Device Name</span>} rules={[formItemRequiredRule]}>
                  <Input placeholder={"Device Name"} />
                </Form.Item>
                <Form.Item className={"w-full"} name={"tenantId"} label={<span className={"text-xs text-gray-400"}>Select Facility</span>} rules={[formItemRequiredRule]}>
                  <Select showSearch={true} optionFilterProp={"children"} placeholder={"Device Facility"}>
                    {authentication?.tenants?.map(({ id, name }) => (
                      <Select.Option title={name} key={id} value={id}>
                        {name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Form>
              <div className={"text-right"}>
                <Button
                  className={PRIMARY_BUTTON_STYLE}
                  onClick={(): void => {
                    setLoading("ADD_DEVICE");
                    form.submit();
                  }}
                >
                  Create Device
                </Button>
              </div>
            </div>
          </div>
        )}
        {deviceSummary && step === 2 && (
          <div className={"flex flex-col gap-6"}>
            <div className={"w-full"}>
              <div className={"flex flex-row gap-4"}>
                <div className={"relative border w-80 md:w-full md:h-48 p-4 mx-auto md:mx-0 bg-white shadow-xs rounded"}>
                  <div className={"flex flex-col md:flex-row h-full"}>
                    <div className={"w-56 h-full"}>
                      <img className={"max-w-full max-h-full mx-auto"} src={selectedModel?.imageUrl} />
                    </div>
                    <Divider type={"vertical"} className={"h-full mx-4"} />
                    <div className={"flex flex-col gap-5"}>
                      <div className={"w-full text-base font-bold"}>
                        <span className={"text-primary-600 group"}>{deviceSummary?.device?.name}</span>
                      </div>
                      <div className={"flex flex-col gap-1"}>
                        <div className={"truncate overflow-ellipsis"}>
                          <div className={"inline-block w-44 pr-1 text-xs"}>ID</div>
                          <span className={"w-full truncate overflow-ellipsis font-light"}>
                            <Tag className={"text-xs"}>{deviceSummary?.device?.id}</Tag>
                          </span>
                        </div>
                        <div className={"truncate overflow-ellipsis"}>
                          <div className={"inline-block w-44 pr-1 text-xs"}>Status</div>
                          <span className={"w-full truncate overflow-ellipsis font-light"}>
                            <Tag className={`text-xs ${deviceSummary?.device?.currentStatus ? STATUS_STYLE[deviceSummary.device.currentStatus.status] : STATUS_STYLE[DeviceStatusType.UNKNOWN]}`}>
                              {getConnectionStatus(deviceSummary?.device?.currentStatus?.status, deviceSummary?.device?.currentStatus?.activeConnections, deviceSummary?.device?.currentStatus?.totalConnections)}{" "}
                            </Tag>
                          </span>
                        </div>
                        <div className={"truncate overflow-ellipsis"}>
                          <div className={"inline-block w-44 pr-1 text-xs"}>Manufacturer</div>
                          <span className={"w-full truncate overflow-ellipsis font-light text-xs"}>{deviceSummary?.device?.model?.manufacturer?.name}</span>
                        </div>
                        <div className={"truncate overflow-ellipsis"}>
                          <div className={"inline-block w-44 pr-1 text-xs"}>Model</div>
                          <span className={"w-full truncate overflow-ellipsis font-light text-xs"}>{deviceSummary?.device?.model?.name}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className={"w-full"}>
              <div className={"font-semibold"}>
                <ApiOutlined className={"text-xs mr-2"} />
                Connections
              </div>
              <Divider className={"mt-2 mb-4"} type={"horizontal"} />
              <DeviceConnections deviceId={deviceSummary?.device?.id} manufacturer={selectedModel?.manufacturer} />
            </div>
          </div>
        )}
      </div>
    </PageLayout>
  );
};

type LoadingType = "INITIAL_DATA" | "ADD_DEVICE";
type getPromiseType = () => Promise<void>;
type getSummaryType = (deviceId: string) => void;
